import { Button, Flex, Form, Link, PasswordField, Spacing, Text, TextField } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import trim from 'lodash/trim';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { PasskeyAuthButton } from 'shared/components';
import { getPath, Path } from 'urlBuilder';
import { z } from 'zod';

import { login } from '../../actions';
import { LoginModuleData } from '../../reducers/page/moduleReducer';
import { getLoading, getModule } from '../../selectors/pageSelectors';
import { emailFieldName, passwordFieldName } from './LoginForm.constants';
import { messages } from './LoginForm.messages';
import { schema } from './LoginForm.schema';

export const LoginForm = memo(() => {
    const dispatch = useDispatch();
    const module = useSelector(getModule<LoginModuleData>);
    const isLoading = useSelector(getLoading);

    const form = useForm<z.infer<typeof schema>>({
        defaultValues: {
            [emailFieldName]: module.email ?? '',
            [passwordFieldName]: '',
        },
        resolver: zodResolver(schema),
    });

    const onSubmit = form.handleSubmit((data) => {
        dispatch(
            login({
                ...data,
                app: module.app,
                postAction: module.postAction,
            })
        );
    });

    const email = form.watch(emailFieldName);

    return (
        <Form form={form} onSubmit={onSubmit} data-qa='login-page__form'>
            <Flex direction='column' spacing='16'>
                <TextField
                    name={emailFieldName}
                    required
                    placeholder={messages.enterEmail}
                    label={messages.email}
                    initFocus
                    clearable={false}
                    inputMode='email'
                    transform={trim}
                />

                <PasswordField
                    name={passwordFieldName}
                    required
                    placeholder={messages.enterPassword}
                    label={messages.password}
                    clearable={false}
                    transform={trim}
                />

                <Flex justifyContent='end'>
                    <Link
                        font='label'
                        fontSize='small'
                        fontWeight='medium'
                        as={RouterLink}
                        to={getPath(Path.resetPassword, encodeURIComponent(email))}
                    >
                        {messages.forgotPassword}
                    </Link>
                </Flex>
            </Flex>

            <Spacing height={24} />

            <Flex direction='column' spacing='24'>
                <Button type='submit' color='mint80' block size='large' progress={isLoading}>
                    {messages.submit}
                </Button>

                <Flex direction='column' spacing='8' alignItems='center' width='100%'>
                    <Text font='label' color='midnight60'>
                        {messages.loginWithLabel}
                    </Text>

                    <PasskeyAuthButton block outline noUppercase size='large' />
                </Flex>
            </Flex>
        </Form>
    );
});

LoginForm.displayName = 'LoginForm';
